/**
 * MobileHomeDashboard v5
 * Matches the reference HTML mockups (dashboard.html, singleoptimize.html).
 * Design language:
 *   - Gradient header (light) / dark surface header (dark) — same as mockup
 *   - Credits pill + avatar in header
 *   - 4-tile KPI strip
 *   - "Next Step" gradient call-to-action card
 *   - Recent activity list (dividers)
 *   - 4-tile Quick Actions grid
 * All colors use the same CSS variables as the web app.
 */
import { Zap, Video, ChevronRight, History, Plus, Target, Users } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../lib/supabase';
import type { DashboardPage } from '../../modules/dashboard/components/DashboardContainer';
import { getApplicationStatistics } from '../../services/applicationTrackingService';
import { getJobReadiness } from '../../services/dashboardInsightsService';
import { getOptimizationStats, getMonthlyActivityForDashboard } from '../../services/optimizationService';
import { getCurrentUserId } from '../../utils/authUtils';

interface MobileHomeDashboardProps {
  onNavigate: (page: DashboardPage) => void;
}

/* ── Quick Actions: icons with header/primary color (no tiles) ─────────── */
type IconComp = React.FC<{ size?: number; color?: string }>;
const QUICK: { id: DashboardPage; Icon: IconComp; label: string }[] = [
  // Optimize Resume now goes through the AI Agent (legacy single-optimize retired).
  { id: 'agent',                 Icon: Zap,          label: 'Optimize Resume' },
  { id: 'prepare-for-interview', Icon: Video,         label: 'Prepare' },
  { id: 'objectives-tasks',      Icon: Target,        label: 'Goals & Tasks' },
  { id: 'collaborate',           Icon: Users,         label: 'Collaborate' },
  { id: 'history',               Icon: History,      label: 'My Activities' },
];

interface RecentItem { title: string; subtitle: string; score?: number }
interface Stats { total: number; applicationCount: number; interviewCount: number; readinessScore: number; credits: number }
interface MonthlyActivity { labels: string[]; counts: number[] }

/* ── KPI tile ───────────────────────────────────────────────────────── */
const KpiTile: React.FC<{ label: string; value: string | number; onClick: () => void }> = ({ label, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flex: 1, background: 'var(--vscode-bg-elevated)',
      border: '1px solid var(--vscode-border-primary)', borderRadius: 14,
      padding: '12px 4px 10px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 4, cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
      boxShadow: 'var(--vscode-shadow-sm)',
    }}
  >
    <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--vscode-text-primary)', lineHeight: 1, letterSpacing: '-0.02em' }}>
      {value}
    </span>
    <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', lineHeight: 1.3 }}>
      {label}
    </span>
  </button>
);

const MobileHomeDashboard: React.FC<MobileHomeDashboardProps> = ({ onNavigate }) => {
  const [name, setName]       = useState<string | null>(null);
  const [stats, setStats]     = useState<Stats>({ total: 0, applicationCount: 0, interviewCount: 0, readinessScore: 0, credits: 0 });
  const [recent, setRecent]   = useState<RecentItem[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyActivity | null>(null);

  const loadData = useCallback(async () => {
    try {
      const userId = await getCurrentUserId();
      if (userId) {
        const { data: profile } = await supabase.from('users').select('name').eq('user_id', userId).single();
        setName(profile?.name?.trim()?.split(/\s+/)[0] ?? null);

        // Fetch last 3 optimizations for recent activity
        const { data: opts } = await supabase
          .from('optimization_history')
          .select('job_title, company_name, ats_score')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(3);
        if (opts) {
          setRecent(opts.map(o => ({
            title:    o.job_title   ?? 'Untitled Resume',
            subtitle: o.company_name ?? 'No company',
            score:    o.ats_score   ?? undefined,
          })));
        }
      }

      const [optStats, appStats, readiness, monthly] = await Promise.allSettled([
        getOptimizationStats(),
        getApplicationStatistics(),
        getJobReadiness(),
        getMonthlyActivityForDashboard(),
      ]);
      if (monthly.status === 'fulfilled' && monthly.value) {
        setMonthlyData(monthly.value);
      }

      // Credits — read directly from users table
      let credits = 0;
      if (userId) {
        const { data: u } = await supabase.from('users').select('credits').eq('user_id', userId).single();
        credits = u?.credits ?? 0;
      }

      const appData = appStats.status === 'fulfilled' ? appStats.value : null;
      const interviewCount = appData
        ? (appData.applicationsByStatus['interview'] ?? 0) + (appData.applicationsByStatus['Interview'] ?? 0)
        : 0;

      setStats({
        total:            optStats.status  === 'fulfilled' ? (optStats.value?.total             ?? 0) : 0,
        applicationCount: appData          ? (appData.totalApplications                          ?? 0) : 0,
        interviewCount,
        readinessScore:   readiness.status === 'fulfilled' ? Math.round(readiness.value?.score  ?? 0) : 0,
        credits,
      });
    } catch (e) {
      console.error('MobileHomeDashboard load error', e);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const hour        = new Date().getHours();
  const greeting    = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const displayName = name ?? 'there';
  const avatarLetter = (name ?? 'U')[0].toUpperCase();

  /* Next Step logic: push to first meaningful action */
  const nextStep = stats.total === 0
    ? { label: 'Optimize your resume', desc: 'Upload a resume and match it to a job', page: 'agent' as DashboardPage }
    : stats.applicationCount === 0
    ? { label: 'Track your applications', desc: 'Log the jobs you have applied to', page: 'history' as DashboardPage }
    : { label: 'Prepare for interviews', desc: `${stats.interviewCount} interview${stats.interviewCount !== 1 ? 's' : ''} tracked · keep practising`, page: 'prepare-for-interview' as DashboardPage };

  return (
    <div style={{ background: 'var(--vscode-bg-primary)', minHeight: '100%' }}>

      {/* ── Header: compact, theme (dark gradient in both themes — no purple) ── */}
      <div
        style={{
          background: 'var(--ggj-header-bg)',
          minHeight: 56,
          borderRadius: 0,
          paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 12px)',
          paddingBottom: 12,
          paddingLeft: 20,
          paddingRight: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ggj-header-sub, rgba(255,255,255,0.6))', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
              {greeting},
            </p>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ggj-header-text, #fff)', margin: '2px 0 0', letterSpacing: '-0.02em', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </h1>
            <p style={{ fontSize: 12, color: 'var(--ggj-header-sub, rgba(255,255,255,0.7))', margin: '2px 0 0' }}>
              Here&apos;s your job search summary
            </p>
          </div>

          {/* Credits pill + avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {stats.credits > 0 && (
              <button
                type="button"
                onClick={() => onNavigate('credits')}
                style={{
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 20, padding: '4px 10px',
                  display: 'flex', alignItems: 'center', gap: 5,
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Zap size={13} color="var(--ggj-header-text, rgba(255,255,255,0.9))" />
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ggj-header-text, rgba(255,255,255,0.95))' }}>
                  {stats.credits}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => onNavigate('profile')}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 800, color: 'var(--ggj-header-text, #fff)',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {avatarLetter}
            </button>
          </div>
        </div>
      </div>

      {/* ── Layered content card (rounded top, -12px overlap, theme bg; scrollbars hidden) ── */}
      <div
        style={{
          marginTop: -12,
          borderRadius: '24px 24px 0 0',
          background: 'var(--ggj-mobile-content-card-bg, var(--vscode-bg-primary))',
          borderTop: 'var(--ggj-mobile-content-card-border-top, none)',
          boxShadow: 'var(--ggj-mobile-content-card-shadow, none)',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div className="mobile-scroll" data-ggj-native-scroll="" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 16, minHeight: '100%' }}>

        {/* Monthly activity — graph first (header color bars) */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--vscode-text-primary)', margin: '0 0 10px' }}>Monthly activity</p>
          <div
            style={{
              background: 'var(--vscode-bg-elevated)',
              border: '1px solid var(--vscode-border-primary)',
              borderRadius: 16,
              padding: '14px 12px 10px',
              boxShadow: 'var(--vscode-shadow-sm)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 88 }}>
              {(() => {
                const rows = monthlyData?.labels?.length
                  ? monthlyData.labels.map((_, i) => ({ label: monthlyData.labels[i], count: monthlyData.counts[i] ?? 0 }))
                  : Array.from({ length: 6 }, (_, i) => {
                      const m = new Date();
                      m.setMonth(m.getMonth() - (5 - i));
                      return { label: m.toLocaleDateString('en-US', { month: 'short' }), count: 0 };
                    });
                const counts = rows.map(r => r.count);
                const max = Math.max(1, ...counts);
                return rows.map(({ label, count }, i) => {
                  const h = max > 0 ? Math.max(4, (count / max) * 60) : 4;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                    <div
                      style={{
                        width: '100%',
                        height: h,
                        minHeight: 4,
                        borderRadius: 6,
                        background: 'var(--ggj-accent-solid, var(--vscode-primary))',
                      }}
                      title={`${label}: ${count}`}
                    />
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--vscode-text-tertiary)' }}>
                      {label}
                    </span>
                  </div>
                );
              });
              })()}
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'flex', gap: 8 }}>
          <KpiTile label="Resumes"    value={stats.total}                onClick={() => onNavigate('history')} />
          <KpiTile label="Applied"    value={stats.applicationCount}     onClick={() => onNavigate('history')} />
          <KpiTile label="Interviews" value={stats.interviewCount}       onClick={() => onNavigate('prepare-for-interview')} />
          <KpiTile label="Readiness"  value={`${stats.readinessScore}%`} onClick={() => onNavigate('history')} />
        </div>

        {/* Next Step card — theme-aware: elevated card + single accent */}
        <button
          type="button"
          onClick={() => onNavigate(nextStep.page)}
          style={{
            background: 'var(--vscode-bg-elevated)',
            border: '1px solid var(--vscode-border-primary)',
            borderRadius: 16,
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            boxShadow: 'var(--vscode-shadow-sm)',
            width: '100%',
            cursor: 'pointer',
            textAlign: 'left',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--vscode-primary)' }}>
            <Zap size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
              Next step
            </p>
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--vscode-text-primary)', margin: '3px 0 2px', lineHeight: 1.3 }}>
              {nextStep.label}
            </p>
            <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)', margin: 0, lineHeight: 1.4 }}>
              {nextStep.desc}
            </p>
          </div>
          <ChevronRight size={18} style={{ flexShrink: 0, color: 'var(--vscode-text-tertiary)' }} />
        </button>

        {/* Recent Activity */}
        {recent.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--vscode-text-primary)' }}>Recent Activity</span>
              <button type="button" onClick={() => onNavigate('history')} style={{ fontSize: 12, fontWeight: 600, color: 'var(--vscode-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                See all
              </button>
            </div>
            <div style={{ background: 'var(--vscode-bg-elevated)', borderRadius: 16, border: '1px solid var(--vscode-border-primary)', overflow: 'hidden', boxShadow: 'var(--vscode-shadow-sm)' }}>
              {recent.map((item, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={{ height: 1, background: 'var(--vscode-border-divider)', margin: '0 16px' }} />}
                  <button
                    type="button"
                    onClick={() => onNavigate('history')}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand-accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <History size={16} style={{ color: 'var(--vscode-primary)' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--vscode-text-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)', margin: '2px 0 0' }}>
                        {item.subtitle}
                      </p>
                    </div>
                    {item.score !== undefined && (
                      <span style={{ fontSize: 13, fontWeight: 800, color: item.score >= 70 ? 'var(--vscode-success)' : item.score >= 50 ? 'var(--vscode-warning)' : 'var(--vscode-error)', flexShrink: 0 }}>
                        {item.score}
                      </span>
                    )}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions — small tiles in 3×3 grid */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--vscode-text-primary)', margin: '0 0 10px' }}>Quick Actions</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {QUICK.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => onNavigate(q.id)}
                style={{
                  background: 'var(--vscode-bg-elevated)',
                  border: '1px solid var(--vscode-border-primary)',
                  borderRadius: 12,
                  padding: '10px 6px 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  boxShadow: 'var(--vscode-shadow-sm)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <q.Icon size={22} color="var(--ggj-accent-solid, var(--vscode-primary))" />
                </span>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--vscode-text-secondary)', lineHeight: 1.2, textAlign: 'center' }}>
                  {q.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Empty state: no activity yet */}
        {recent.length === 0 && stats.total === 0 && (
          <button
            type="button"
            onClick={() => onNavigate('agent')}
            style={{
              background: 'var(--vscode-bg-elevated)', border: '1px dashed var(--vscode-border-primary)',
              borderRadius: 16, padding: '24px 20px', textAlign: 'center', cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent', width: '100%',
            }}
          >
            <Plus size={32} style={{ color: 'var(--vscode-text-tertiary)', margin: '0 auto 8px' }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--vscode-text-secondary)', margin: 0 }}>Optimize your first resume</p>
            <p style={{ fontSize: 12, color: 'var(--vscode-text-tertiary)', margin: '4px 0 0' }}>Tap to get started</p>
          </button>
        )}

        </div>
      </div>
    </div>
  );
};

export default MobileHomeDashboard;
